import { useEffect, useRef, useCallback } from 'react';
import { Circle } from 'fabric';
import { usePlant } from '../../context/PlantContext';
import { useGrid } from '../../context/GridContext';
import { useCommand } from '../../context/CommandContext';
import { PlantCircle, parseSpacingValue } from './objects/PlantCircle';
import { CreateObjectCommand } from '../../commands/CreateObjectCommand';
import { wouldOverlapAtPosition } from './utils/overlapDetection';
import { snapValue } from './tools/snapUtils';
import { isCoarsePointer } from '../../hooks/useHasCoarsePointer';

/**
 * PlantPlacementManager - Manages click-to-place plant placement
 *
 * Responsibilities:
 * - Listen to placement mode from PlantContext
 * - Show instruction overlay when in placement mode
 * - Render ghost circle following cursor
 * - Handle canvas click to place plant
 * - Handle ESC key to cancel placement
 * - Create PlantCircle on canvas with CreateObjectCommand (undo/redo)
 * - Update overlap detection after placement
 *
 * Visual Feedback:
 * - Instruction overlay: "Klicken Sie auf die Leinwand, um die Pflanze zu platzieren"
 * - Canvas border glow: 2px solid Emerald-500
 * - Cursor: crosshair
 * - Ghost circle: follows cursor, shows plant icon + tooltip
 *
 * Integration:
 * - Used by GardenCanvas component
 * - Works with PlantCard (catalog) and CompactPlantCard (favorites)
 */
const PlantPlacementManager = ({ canvas, isActive }) => {
  const {
    placementMode,
    selectedPlantForPlacement,
    exitPlacementMode,
    cancelPlacementMode,
  } = usePlant();

  const { snapEnabled, gridSize } = useGrid();
  const { commandStack } = useCommand();

  const ghostInnerRef = useRef(null);
  const ghostOuterRef = useRef(null);
  // Tracks whether the ghosts have been added to the canvas yet
  const ghostAddedRef = useRef(false);
  // Always-on tracker: keeps the last known cursor position in world coordinates
  const lastPointerRef = useRef(null);

  // Touch bookkeeping (#748). On touch the plant is placed on lift (`mouse:up`)
  // rather than `mouse:down`, so a two-finger pinch-zoom started during placement
  // doesn't drop a plant on the first finger's touchdown. `gestureCancelledRef` is
  // set once a second finger appears and blocks the pending placement on lift.
  const activeTouchCountRef = useRef(0);
  const gestureCancelledRef = useRef(false);

  // Refs mirror context/prop state so handlers can stay reference-stable
  // (avoids re-running the main effect on every parent rerender — #429).
  const placementModeRef = useRef(placementMode);
  const selectedPlantRef = useRef(selectedPlantForPlacement);
  const snapEnabledRef = useRef(snapEnabled);
  const gridSizeRef = useRef(gridSize);
  const commandStackRef = useRef(commandStack);
  const exitPlacementModeRef = useRef(exitPlacementMode);
  const cancelPlacementModeRef = useRef(cancelPlacementMode);

  useEffect(() => { placementModeRef.current = placementMode; }, [placementMode]);
  useEffect(() => { selectedPlantRef.current = selectedPlantForPlacement; }, [selectedPlantForPlacement]);
  useEffect(() => { snapEnabledRef.current = snapEnabled; }, [snapEnabled]);
  useEffect(() => { gridSizeRef.current = gridSize; }, [gridSize]);
  useEffect(() => { commandStackRef.current = commandStack; }, [commandStack]);
  useEffect(() => { exitPlacementModeRef.current = exitPlacementMode; }, [exitPlacementMode]);
  useEffect(() => { cancelPlacementModeRef.current = cancelPlacementMode; }, [cancelPlacementMode]);

  /**
   * Always-on pointer tracker — runs whenever the canvas is active.
   * Keeps lastPointerRef up to date so placement mode can show the ghost
   * immediately at the correct position, even if the mouse is stationary.
   */
  useEffect(() => {
    if (!canvas || !isActive) return;
    const trackPointer = (e) => {
      lastPointerRef.current = canvas.getScenePoint(e.e);
    };
    canvas.on('mouse:move', trackPointer);
    return () => canvas.off('mouse:move', trackPointer);
  }, [canvas, isActive]);

  /**
   * Snap position to grid if snap is enabled and Ctrl is not pressed.
   * Reads from refs so the function identity stays stable across rerenders.
   */
  const getSnappedPosition = useCallback((pointer, ctrlPressed) => {
    if (!snapEnabledRef.current || ctrlPressed) {
      return { x: pointer.x, y: pointer.y };
    }

    return {
      x: snapValue(pointer.x, gridSizeRef.current, true, false),
      y: snapValue(pointer.y, gridSizeRef.current, true, false),
    };
  }, []);

  /**
   * Handle canvas click - place plant at cursor position
   */
  const handleCanvasClick = useCallback((e) => {
    const placement = placementModeRef.current;
    const plant = selectedPlantRef.current;
    if (!placement || !plant || !canvas) {
      return;
    }

    // Touch: a multi-finger gesture (pinch-zoom) invalidated this tap — skip the
    // placement on lift instead of dropping a plant mid-zoom (#748).
    if (gestureCancelledRef.current) {
      gestureCancelledRef.current = false;
      return;
    }

    const pointer = canvas.getScenePoint(e.e);
    const ctrlPressed = e.e.ctrlKey;

    // Snap to grid if enabled
    const snappedPos = getSnappedPosition(pointer, ctrlPressed);

    // Create PlantCircle — default color = first flower_color from DB (or null = emerald)
    const flowerColors = plant.properties?.flower_color;
    const defaultColor = Array.isArray(flowerColors) && flowerColors.length > 0 ? flowerColors[0] : null;
    const plantCircle = new PlantCircle(plant, {
      left: snappedPos.x,
      top: snappedPos.y,
      selectedColor: defaultColor,
    });

    // Use CreateObjectCommand for undo/redo support
    // Note: CreateObjectCommand.execute() will automatically call updateOverlapStates()
    const command = new CreateObjectCommand(canvas, plantCircle);
    commandStackRef.current.execute(command);

    // Exit placement mode
    exitPlacementModeRef.current();

    // Select the newly placed plant
    canvas.setActiveObject(plantCircle);
    canvas.requestRenderAll();
  }, [canvas, getSnappedPosition]);

  /**
   * Handle mouse move - update ghost circle positions and overlap colors
   */
  const handleMouseMove = useCallback((e) => {
    const plant = selectedPlantRef.current;
    if (!placementModeRef.current || !plant || !canvas || !ghostInnerRef.current) {
      return;
    }

    const pointer = canvas.getScenePoint(e.e);
    const ctrlPressed = e.e.ctrlKey;
    const snappedPos = getSnappedPosition(pointer, ctrlPressed);

    // Add to canvas on first move if not already added
    if (!ghostAddedRef.current) {
      if (ghostOuterRef.current) canvas.add(ghostOuterRef.current);
      canvas.add(ghostInnerRef.current);
      ghostAddedRef.current = true;
    }

    // Move both ghosts to cursor position
    ghostInnerRef.current.set({ left: snappedPos.x, top: snappedPos.y });
    if (ghostOuterRef.current) {
      ghostOuterRef.current.set({ left: snappedPos.x, top: snappedPos.y });
    }

    // Check overlap state at cursor position and update ghost colors
    const properties = plant.properties || {};
    const innerR = ghostInnerRef.current.radius;
    const outerR = ghostOuterRef.current ? ghostOuterRef.current.radius : innerR;
    const growthType = (properties.care_growth_type || properties.growth_type || '').toLowerCase() || null;

    const state = wouldOverlapAtPosition(snappedPos.x, snappedPos.y, innerR, outerR, growthType, canvas);

    // Update ghost colors based on overlap state
    if (state === 'critical') {
      ghostInnerRef.current.set({ fill: 'rgba(239, 68, 68, 0.3)', stroke: '#EF4444' });
      if (ghostOuterRef.current) ghostOuterRef.current.set({ stroke: '#EF4444' });
    } else if (state === 'warning') {
      ghostInnerRef.current.set({ fill: 'rgba(16, 185, 129, 0.3)', stroke: '#10B981' });
      if (ghostOuterRef.current) ghostOuterRef.current.set({ stroke: '#F59E0B' });
    } else {
      ghostInnerRef.current.set({ fill: 'rgba(16, 185, 129, 0.3)', stroke: '#10B981' });
      if (ghostOuterRef.current) ghostOuterRef.current.set({ stroke: '#10B981' });
    }

    canvas.requestRenderAll();
  }, [canvas, getSnappedPosition]);

  /**
   * Handle ESC key - cancel placement mode. Uses cancelPlacementMode (not exit)
   * so a placement started from the catalog reopens the catalog with the user's
   * filters intact (#764).
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && placementModeRef.current) {
      cancelPlacementModeRef.current();
    }
  }, []);

  /**
   * Create ghost circles (inner + outer) for visual feedback.
   * Not added to canvas here — added lazily once cursor position is known.
   * Ghosts are tagged with `data.isGhost = true` so the layer system and the
   * TabBar object counter both skip them (#429 — prevents counter loop).
   *
   * @returns {{ inner: Circle, outer: Circle|null }} Ghost circle objects
   */
  const createGhostCircles = useCallback(() => {
    const plant = selectedPlantRef.current;
    if (!canvas || !plant) {
      return { inner: null, outer: null };
    }

    const properties = plant.properties || {};
    const spacingCm = parseSpacingValue(properties.space_requirement_cm);
    const minWidthCm = plant.min_width_cm ?? properties.min_width_cm ?? null;
    const maxWidthCm = plant.max_width_cm ?? properties.max_width_cm ?? null;

    let innerRadius, outerRadius;
    if (minWidthCm != null && maxWidthCm != null && minWidthCm > 0 && maxWidthCm > 0) {
      innerRadius = minWidthCm / 2;
      outerRadius = maxWidthCm / 2;
    } else {
      innerRadius = spacingCm / 2;
      outerRadius = spacingCm / 2;
    }

    const ghostInner = new Circle({
      radius: innerRadius,
      fill: 'rgba(16, 185, 129, 0.3)',
      stroke: '#10B981',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      data: { isGhost: true },
    });

    // Only create outer ghost if radii differ
    let ghostOuter = null;
    if (outerRadius > innerRadius) {
      ghostOuter = new Circle({
        radius: outerRadius,
        fill: 'transparent',
        stroke: '#10B981',
        strokeWidth: 1.5,
        strokeDashArray: [8, 4],
        opacity: 0.6,
        left: 0,
        top: 0,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
        data: { isGhost: true },
      });
    }

    return { inner: ghostInner, outer: ghostOuter };
  }, [canvas]);

  /**
   * Remove ghost circles from canvas
   */
  const removeGhostCircles = useCallback(() => {
    if (canvas) {
      if (ghostOuterRef.current) {
        canvas.remove(ghostOuterRef.current);
        ghostOuterRef.current = null;
      }
      if (ghostInnerRef.current) {
        canvas.remove(ghostInnerRef.current);
        ghostInnerRef.current = null;
      }
      canvas.requestRenderAll();
    }
  }, [canvas]);

  /**
   * Effect: Setup placement mode
   * - Add event listeners
   * - Create ghost circle (add immediately if cursor position is known)
   * - Change cursor
   * - Show instruction overlay
   *
   * Deps are intentionally narrow (canvas, isActive, placementMode, selectedPlantForPlacement).
   * The handler callbacks are reference-stable via refs, so they don't trigger
   * effect re-runs on every parent rerender (#429).
   */
  // eslint-disable-next-line react-hooks/immutability -- imperative Fabric canvas config (cursor/selection) for placement mode; Canvas is not a FabricObject so .set() is unavailable, and changes are reverted in cleanup
  useEffect(() => {
    if (!canvas || !isActive || !placementMode || !selectedPlantForPlacement) {
      // Clean up if not in placement mode
      removeGhostCircles();
      return;
    }

    // Create ghost circles (inner + outer)
    const { inner, outer } = createGhostCircles();
    ghostInnerRef.current = inner;
    ghostOuterRef.current = outer;
    ghostAddedRef.current = false;

    // If the cursor was already over the canvas, show the ghosts immediately
    // at the last known position — no mouse:move needed, no jump.
    if (lastPointerRef.current) {
      const snappedPos = getSnappedPosition(lastPointerRef.current, false);
      if (inner) inner.set({ left: snappedPos.x, top: snappedPos.y });
      if (outer) {
        outer.set({ left: snappedPos.x, top: snappedPos.y });
        canvas.add(outer);
      }
      if (inner) canvas.add(inner);
      ghostAddedRef.current = true;
      canvas.requestRenderAll();
    }

    // Change cursor to crosshair
    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric canvas config; reverted in cleanup
    canvas.defaultCursor = 'crosshair';
    canvas.hoverCursor = 'crosshair';
    canvas.setCursor('crosshair');

    // Disable selection while in placement mode
    canvas.selection = false;
    canvas.forEachObject((obj) => {
      obj.selectable = false;
    });

    // On touch, place on lift (mouse:up) so a two-finger pinch that starts during
    // placement can cancel the tap; on desktop keep the immediate mouse:down (#748).
    const coarse = isCoarsePointer();
    const placeEvent = coarse ? 'mouse:up' : 'mouse:down';
    activeTouchCountRef.current = 0;
    gestureCancelledRef.current = false;

    // Track fingers so a pinch (2+ touches) marks the pending tap as cancelled.
    const upperCanvas = canvas.upperCanvasEl;
    const handleTouchStart = (te) => {
      const count = te.touches?.length ?? 0;
      activeTouchCountRef.current = count;
      if (count >= 2) {
        gestureCancelledRef.current = true;
      } else if (count === 1) {
        gestureCancelledRef.current = false; // fresh single-finger tap
      }
    };
    const handleTouchEnd = (te) => {
      activeTouchCountRef.current = te.touches?.length ?? 0;
    };
    if (coarse && upperCanvas) {
      upperCanvas.addEventListener('touchstart', handleTouchStart, { passive: true });
      upperCanvas.addEventListener('touchend', handleTouchEnd, { passive: true });
      upperCanvas.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    }

    // Add event listeners
    canvas.on(placeEvent, handleCanvasClick);
    canvas.on('mouse:move', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      canvas.off(placeEvent, handleCanvasClick);
      canvas.off('mouse:move', handleMouseMove);
      if (coarse && upperCanvas) {
        upperCanvas.removeEventListener('touchstart', handleTouchStart);
        upperCanvas.removeEventListener('touchend', handleTouchEnd);
        upperCanvas.removeEventListener('touchcancel', handleTouchEnd);
      }
      window.removeEventListener('keydown', handleKeyDown);
      ghostAddedRef.current = false;

      // Restore cursor
      canvas.defaultCursor = 'default';
      canvas.hoverCursor = 'move';

      // Re-enable selection
      canvas.selection = true;
      canvas.forEachObject((obj) => {
        // Only re-enable if object was originally selectable
        // (background images, grid lines, and snap guides should stay non-selectable)
        if (
          obj.data?.type !== 'wizard-background' &&
          !obj.data?.isGridLine &&
          !obj.data?.isSnapGuide
        ) {
          obj.selectable = true;
        }
      });

      // Remove ghost circles
      removeGhostCircles();
    };
  }, [
    canvas,
    isActive,
    placementMode,
    selectedPlantForPlacement,
    getSnappedPosition,
    handleCanvasClick,
    handleMouseMove,
    handleKeyDown,
    createGhostCircles,
    removeGhostCircles,
  ]);

  // This component renders nothing - it only manages canvas interaction
  // The instruction overlay and canvas border glow are handled by CSS classes
  // applied to the canvas container in GardenCanvas component
  return null;
};

export default PlantPlacementManager;
